// src/registered/site-admin/meter_catalogue/MeterCatalogueDTO.js
//
// Meter Data Catalogue — domain model.
//
// A "meter concept" is a stable, protocol-neutral name for one measurable
// utility-meter quantity (energy.active.import.total, gas.volume.total, ...).
// External scheme identifiers (OBIS today; M-Bus / CIM / SMETS2 later) are
// *mapped onto* a concept, never used as its key. Each concept can be linked to
// one or more ESG AttributeDefinitions so resolved meter readings flow into the
// declaration pipeline (Z_12 -> Z_11 -> Z_13 -> XBRL).
//
// Backed by ESG.MeterConcept* / ESG.MeterRegistration in nrgpi_sustina (RDB2) —
// see .claude/db/create_meter_catalogue.sql. Same site-admin feature shape as
// knowledge_base / report_component_library.

// Fallback pick-lists — live values come from the Ref.ESG_* tables via the
// service; these keep the screen usable if the lookup call fails.
export const COMMODITIES = [
  { code: "electricity", label: "Electricity" },
  { code: "gas", label: "Gas" },
  { code: "water", label: "Water" },
  { code: "heat", label: "Heat" },
  { code: "cooling", label: "Cooling" },
];

export const IDENTIFIER_SCHEMES = [
  { code: "OBIS", label: "OBIS (IEC 62056)" },
  { code: "MBUS", label: "M-Bus (EN 13757)" },
  { code: "CIM", label: "CIM ReadingType (IEC 61968-9)" },
  { code: "SMETS2", label: "SMETS2 / DCC" },
  { code: "MANUFACTURER", label: "Manufacturer" },
];

export const CONCEPT_STATUSES = [
  { code: "draft", label: "Draft" },
  { code: "candidate", label: "Candidate" },
  { code: "standard", label: "Standard" },
  { code: "verified", label: "Verified" },
  { code: "deprecated", label: "Deprecated" },
];

export const AGGREGATION_METHODS = [
  { code: "SUM", label: "Sum" },
  { code: "AVERAGE", label: "Average" },
  { code: "WEIGHTED_AVERAGE", label: "Weighted average" },
  { code: "LATEST", label: "Latest" },
  { code: "MAX", label: "Maximum" },
  { code: "MIN", label: "Minimum" },
  { code: "COUNT", label: "Count" },
];

export const QUANTITIES = [
  "energy", "power", "voltage", "current", "frequency",
  "volume", "flow", "temperature", "pressure", "powerfactor", "count", "duration",
];

export const ELECTRICAL_CHARACTERISTICS = ["", "active", "reactive", "apparent"];
export const DIRECTIONS = ["", "import", "export", "net", "notapplicable"];
export const PHASES = ["", "l1", "l2", "l3", "neutral", "aggregate", "notapplicable"];
export const TIME_CHARACTERISTICS = [
  "", "instantaneous", "total", "interval", "average", "minimum", "maximum",
  "maximumdemand", "historical", "billingperiod",
  "tariff1", "tariff2", "tariff3", "tariff4",
];
export const READING_KINDS = ["REGISTER", "INTERVAL", "INSTANT"];

// A dotted, lowercase, [a-z0-9.] key of 2+ segments.
export const CONCEPT_KEY_RE = /^[a-z][a-z0-9]*(\.[a-z0-9]+)+$/;

let _seq = 0;
export const newLocalId = (prefix = "mc") => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  _seq += 1;
  return `${prefix}_${Date.now()}_${_seq}`;
};

/** Split an OBIS display string "A-B:C.D.E*F" (or ".F") into numeric parts. */
export const parseObis = (display) => {
  const m = String(display || "").trim().match(
    /^(\d{1,3})-(\d{1,3}):(\d{1,3})\.(\d{1,3})\.(\d{1,3})(?:[*.](\d{1,3}))?$/,
  );
  if (!m) return null;
  const [, a, b, c, d, e, f] = m;
  return {
    a: +a, b: +b, c: +c, d: +d, e: +e,
    f: f === undefined ? 255 : +f,
  };
};

export class MeterConceptIdentifierDTO {
  constructor({
    identifierId = null,
    schemeCode = "OBIS",
    displayCode = "",
    mappingContext = "abstract",
    obisA = null, obisB = null, obisC = null, obisD = null, obisE = null, obisF = null,
    cosemInterfaceClass = "",
    cosemClassId = null,
    cosemAttribute = null,
    standard = "",
    statusCode = "candidate",
    mappingMethod = "MANUAL",
    confidence = null,
    verified = false,
    notes = "",
  } = {}) {
    this.identifierId = identifierId || newLocalId("id");
    this.schemeCode = schemeCode;
    this.displayCode = displayCode;
    this.mappingContext = mappingContext || "abstract";
    this.obisA = obisA; this.obisB = obisB; this.obisC = obisC;
    this.obisD = obisD; this.obisE = obisE; this.obisF = obisF;
    this.cosemInterfaceClass = cosemInterfaceClass || "";
    this.cosemClassId = cosemClassId;
    this.cosemAttribute = cosemAttribute;
    this.standard = standard || "";
    this.statusCode = statusCode || "candidate";
    this.mappingMethod = mappingMethod || "MANUAL";
    this.confidence = confidence;
    this.verified = !!verified;
    this.notes = notes || "";
  }
}

export class MeterConceptAttributeMapDTO {
  constructor({ mapId = null, attributeDefinitionId = "", confidence = null, notes = "" } = {}) {
    this.mapId = mapId || newLocalId("map");
    this.attributeDefinitionId = attributeDefinitionId || "";
    this.confidence = confidence;
    this.notes = notes || "";
  }
}

export default class MeterConceptDTO {
  constructor({
    meterConceptId = null,
    conceptKey = "",
    label = "",
    description = "",
    commodityCode = "electricity",
    quantity = "energy",
    electricalCharacteristic = "",
    direction = "",
    phase = "",
    timeCharacteristic = "",
    canonicalUnitCode = "",
    defaultAggregationMethodCode = "SUM",
    readingKindHint = "REGISTER",
    statusCode = "draft",
    replacedByMeterConceptId = null,
    isActive = true,
    identifiers = [],
    attributeMaps = [],
    aliases = [],
    createdAtUtc = null,
    updatedAtUtc = null,
  } = {}) {
    this.meterConceptId = meterConceptId || newLocalId("mc");
    this.conceptKey = conceptKey;
    this.label = label;
    this.description = description || "";
    this.commodityCode = commodityCode;
    this.quantity = quantity;
    this.electricalCharacteristic = electricalCharacteristic || "";
    this.direction = direction || "";
    this.phase = phase || "";
    this.timeCharacteristic = timeCharacteristic || "";
    this.canonicalUnitCode = canonicalUnitCode;
    this.defaultAggregationMethodCode = defaultAggregationMethodCode || "SUM";
    this.readingKindHint = READING_KINDS.includes(readingKindHint) ? readingKindHint : "REGISTER";
    this.statusCode = statusCode || "draft";
    this.replacedByMeterConceptId = replacedByMeterConceptId || null;
    this.isActive = isActive !== false;
    this.identifiers = (Array.isArray(identifiers) ? identifiers : []).map(
      (i) => new MeterConceptIdentifierDTO(i),
    );
    this.attributeMaps = (Array.isArray(attributeMaps) ? attributeMaps : []).map(
      (m) => new MeterConceptAttributeMapDTO(m),
    );
    this.aliases = Array.isArray(aliases) ? aliases : [];
    this.createdAtUtc = createdAtUtc;
    this.updatedAtUtc = updatedAtUtc;
  }

  toSummary() {
    return {
      meterConceptId: this.meterConceptId,
      conceptKey: this.conceptKey,
      label: this.label,
      commodityCode: this.commodityCode,
      canonicalUnitCode: this.canonicalUnitCode,
      statusCode: this.statusCode,
      identifierCount: this.identifiers.length,
      attributeMapCount: this.attributeMaps.length,
      isActive: this.isActive,
    };
  }
}

export class MeterRegistrationDTO {
  constructor({
    meterRegistrationId = null,
    meterSerial = "",
    corporateEntityId = "",
    siteReference = "",
    commodityCode = "electricity",
    schemeCode = "",
    meterTimeZone = "",
    description = "",
    isActive = true,
    readingCount = 0,
  } = {}) {
    this.meterRegistrationId = meterRegistrationId || newLocalId("reg");
    this.meterSerial = meterSerial;
    this.corporateEntityId = corporateEntityId;
    this.siteReference = siteReference || "";
    this.commodityCode = commodityCode;
    this.schemeCode = schemeCode || "";
    this.meterTimeZone = meterTimeZone || "";
    this.description = description || "";
    this.isActive = isActive !== false;
    this.readingCount = readingCount || 0;
  }
}
